import { Stack, Typography } from '@mui/material';
import { ReactNode, useId } from 'react';

/** Attributi che legano il controllo della riga alla sua etichetta e alla spiegazione. */
export interface AriaRigaVisualizzazione {
  'aria-labelledby': string;
  'aria-describedby': string | undefined;
}

interface RigaVisualizzazioneProps {
  /** Etichetta della riga: «Presenze», «Ore». */
  etichetta: string;
  /**
   * Spiegazione a vista sotto il controllo, che è anche la sua descrizione per
   * gli screen reader; assente, niente spiegazione e niente descrizione.
   */
  spiegazione?: string;
  /** Il controllo della riga, con gli attributi da mettergli. */
  children: (aria: AriaRigaVisualizzazione) => ReactNode;
}

/**
 * Riga del riquadro Visualizzazione: etichetta sopra, controllo a tutta
 * larghezza (248px), spiegazione sotto. L'etichetta sta sopra e non a
 * sinistra perché accanto all'etichetta «Presenze» il selettore a tre voci
 * non ci starebbe. La spiegazione è a vista e non in un tooltip: nel Mese
 * dice perché le voci sono disabilitate, e su tablet un tocco seleziona la
 * voce senza mai aprire il tooltip. Il controllo prende il nome dall'etichetta
 * (aria-labelledby) e la descrizione dalla spiegazione (aria-describedby).
 */
export function RigaVisualizzazione({
  etichetta,
  spiegazione,
  children,
}: RigaVisualizzazioneProps): JSX.Element {
  const idEtichetta = useId();
  const idSpiegazione = useId();

  return (
    <Stack spacing={0.5}>
      {/* subtitle2 come le intestazioni di PopoverPresenze. Un paragrafo e
          non un titolo (subtitle2 sarebbe un h6): è l'etichetta del
          controllo, sotto il titolo del riquadro */}
      <Typography id={idEtichetta} variant="subtitle2" component="p">
        {etichetta}
      </Typography>
      {children({
        'aria-labelledby': idEtichetta,
        'aria-describedby': spiegazione ? idSpiegazione : undefined,
      })}
      {/* Dopo il controllo e fuori, altrimenti entrerebbe nel suo nome; un
          paragrafo e non uno span in linea. Nel colore del testo, come le
          istruzioni. Quando arriva o se ne va (cambio di vista) il controllo
          resta al suo posto e non si rimonta */}
      {spiegazione && (
        <Typography id={idSpiegazione} variant="caption" component="p">
          {spiegazione}
        </Typography>
      )}
    </Stack>
  );
}
